import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { MessageSquare, FileText, Bell, Search, Settings, LogOut, Menu, X, Calendar, BarChart3, Building2, Shield, Truck } from 'lucide-react';

const AdminDashboardLayoutSobre = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const userName = localStorage.getItem('userName') || 'Administrateur';
  const userEmail = localStorage.getItem('userEmail') || '';
  const initials = userName.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2);

  const menuItems = [
    { icon: BarChart3, label: 'Tableau de bord', path: '/admin', available: true },
    { icon: Building2, label: 'Gestion du Réseau', path: '/admin/network', available: true },
    { icon: Calendar, label: 'Planning de garde', path: '/admin/dutschedule', available: true },
    { icon: MessageSquare, label: 'Messagerie', path: '/admin/messages', available: true },
    { icon: FileText, label: 'Rapports', path: '/admin/rapport', available: true },
    { icon: Settings, label: 'Paramètres', path: '/admin/settings', available: true },
    { icon: Truck, label: 'Livraisons', path: '/admin/livraisons', available: true },
    { icon: Shield, label: 'Super Admin', path: '/super-admin', available: true, highlight: true },
  ];

  const handleMenuClick = (item) => {
    if (item.available) {
      navigate(item.path);
    } else {
      alert('Fonctionnalité pas encore disponible');
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full bg-white shadow-sm transition-all duration-300 z-50 border-r border-gray-200 ${sidebarOpen ? 'w-64' : 'w-0'} overflow-hidden`}>
        <div className="p-6">
          {/* Logo */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                <Building2 className="text-white" size={24} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">SunuFarmasi</h2>
                <p className="text-xs text-gray-500">Admin Panel</p>
              </div>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden">
              <X size={24} />
            </button>
          </div>

          {/* Menu */}
          <nav className="space-y-1">
            {menuItems.map((item, index) => (
              <button
                key={index}
                onClick={() => handleMenuClick(item)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all font-medium ${
                  isActive(item.path)
                    ? 'bg-green-50 text-green-600'
                    : item.highlight
                    ? 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* User + Logout */}
        <div className="absolute bottom-0 w-full p-4 border-t border-gray-200">
          <div className="flex items-center space-x-3 mb-3 px-2">
            <div className="w-9 h-9 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{userName}</p>
              {userEmail && <p className="text-xs text-gray-500 truncate">{userEmail}</p>}
            </div>
          </div>
          <button onClick={handleLogout} className="w-full flex items-center space-x-3 px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-all">
            <LogOut size={18} />
            <span className="font-medium text-sm">Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-0'}`}>
        {/* Header */}
        <header className="bg-white shadow-sm sticky top-0 z-40 border-b border-gray-200">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center space-x-4">
              <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-gray-100 rounded-lg">
                <Menu size={24} />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Bonjour, {userName}</h1>
                <p className="text-sm text-gray-500">Panneau d'administration</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="relative hidden md:block">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 w-56 text-sm"
                />
              </div>
              <button className="relative p-2 hover:bg-gray-100 rounded-lg">
                <Bell size={22} className="text-gray-600" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>
              <div className="flex items-center space-x-3 pl-4 border-l border-gray-200">
                <div className="w-9 h-9 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {initials}
                </div>
                <div className="hidden md:block">
                  <p className="text-sm font-medium text-gray-900">{userName}</p>
                  <p className="text-xs text-gray-500">Administrateur</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        <Outlet />
      </div>
    </div>
  );
};

export default AdminDashboardLayoutSobre;
